import type { SubInstanceOrganizationPermissionType } from "ls/features/share/workspaceOrganizationPermissions";
import type { WorkspacePermissionType } from "ls/features/share/workspacePermissions";
import type { ShareLinkAccessType } from "ls/features/share/workspaceShareLinkAccess";
import type { WorkspaceViewFilterType } from "ls/features/workspace-table/workspaceViewFilter";
import type { RequestOptions } from "../common/server";
import type { IdOrFilesystemId } from "./types";
import type { ListJs } from "@/common/models";
import type {
  CreateFilesystemRequest,
  DeleteFilesystemItemsRequest,
  FilesystemDefaultMappingType,
  FilesystemFolderInfo,
  FilesystemJs,
  FilesystemNotificationStateJs,
  FilesystemRootListingJs,
  FilesystemSearchDataJs,
  FolderNavJs,
  FolderRowJs,
  InsertLocation,
  InvitationJs,
  ListingJs,
  MoveFilesystemItemsRequest,
  MoveFilesystemItemsResponse,
  RecentJs,
  RequestInviteResponse,
  ResponseObject,
  ShareFilesystemResultResponse,
  ShortcutJs,
  ShortcutType,
  WorkspaceSecurityJs,
} from "@/generated/models";
import { toCloudDefaultMappingType } from "ls/features/share/workspaceOrganizationPermissions";
import { toCloudUserViewOnly } from "ls/features/share/workspaceShareLinkAccess";
import { toCloudWorkspaceViewFilter } from "ls/features/workspace-table/workspaceViewFilter";
import { objectToFormData } from "@/services/utils/formData";
import { server } from "./server";

const DEFAULT_RECENTS_LIMIT: number = 25;
export async function getRecents(options: { type?: ShortcutType; limit?: number } = {}, { signal }: RequestOptions = {}) {
  const searchParams = {
    type: options.type,
    limit: options.limit ?? DEFAULT_RECENTS_LIMIT,
  };
  const response = await server.get("fs/getRecents", { searchParams, signal });
  return await response.json() as ListJs<RecentJs>;
}

export async function createRecent(shortcut: string) {
  const formData = objectToFormData({ shortcut });
  const response = await server.post("fs/createRecent", { body: formData });
  return await response.json() as ResponseObject;
}

export async function getUserWorkspaces({ signal }: RequestOptions = {}) {
  const searchParams = {
    skipEntryCount: true,
    includeIcons: true,
  };
  const response = await server.get("fs/getUserWorkspaces", { searchParams, signal });
  return await response.json() as ListingJs;
}

export async function getUserWorkspacesNotificationState({ signal }: RequestOptions = {}) {
  const response = await server.get("fs/getuserworkspacesnotificationstate", { signal });
  return await response.json() as FilesystemNotificationStateJs[];
}

export async function getUserOrganizationWorkspaces(options: { organizationId: string }, { signal }: RequestOptions = {}) {
  const searchParams = {
    organizationId: options.organizationId,
  };
  const response = await server.get("fs/getUserOrganizationWorkspaces", { searchParams, signal });
  return await response.json() as ListingJs;
}

export async function getWorkspace(options: { workspaceId: string }, { signal }: RequestOptions = {}) {
  const searchParams = {
    filesystemId: options.workspaceId,
  };
  const response = await server.get("fs/getWorkspace", { searchParams, signal });
  return await response.json() as FilesystemJs;
}

export async function getWorkspaceFile(options: { workspaceId: string }, { signal }: RequestOptions = {}) {
  const searchParams = {
    filesystemId: options.workspaceId,
  };
  const response = await server.get("fs/getFilesystemFile", { searchParams, signal });
  return await response.json() as ShortcutJs;
}

export async function getFilesystemRootFolderContents(options: { workspaceId: string; workspaceViewFilter: WorkspaceViewFilterType; assigneeFilter?: string[] }, { signal }: RequestOptions = {}) {
  const searchParams = new URLSearchParams();
  searchParams.append("filesystemId", options.workspaceId);
  searchParams.append("workspaceViewFilter", toCloudWorkspaceViewFilter(options.workspaceViewFilter).toString());
  if (options.assigneeFilter) {
    options.assigneeFilter.forEach(filter => {
      searchParams.append("assigneeFilter", filter);
    });
  }
  const response = await server.get(`fs/getFilesystemRootFolderContents`, { searchParams, signal });
  return await response.json() as FilesystemRootListingJs;
}

export async function getFolderNavByShortcutId(shortcut: string, { signal }: RequestOptions = {}) {
  const response = await server.get("fs/getFolderNavByShortcutId", {
    searchParams: {
      shortcut,
    },
    signal,
  });
  return await response.json() as FolderNavJs;
}

export async function getFolderNavById(folderId: string, { signal }: RequestOptions = {}) {
  const response = await server.get("fs/getFolderNavById", {
    searchParams: {
      folderId,
    },
    signal,
  });
  return await response.json() as FolderNavJs;
}

export async function getFilesystemNodeContents(id: string, options: { workspaceViewFilter: WorkspaceViewFilterType; assigneeFilter?: string[] }, { signal }: RequestOptions = {}) {
  const searchParams = new URLSearchParams();
  searchParams.append("folderId", id);
  searchParams.append("workspaceViewFilter", toCloudWorkspaceViewFilter(options.workspaceViewFilter).toString());
  if (options.assigneeFilter) {
    options.assigneeFilter.forEach(filter => {
      searchParams.append("assigneeFilter", filter);
    });
  }
  const response = await server.get("fs/getFilesystemNodeContents", {
    searchParams,
    signal,
  });
  return await response.json() as ListingJs;
}

export async function getWorkspaceSecurity(options: { workspaceId: string }, { signal }: RequestOptions = {}) {
  const searchParams = {
    filesystemId: options.workspaceId,
  };
  const response = await server.get("fs/workspaceSecurity", { searchParams, signal });
  return await response.json() as WorkspaceSecurityJs;
}

export async function workspaceCreate(
  request: CreateFilesystemRequest,
) {
  const formData = objectToFormData(request);
  const response = await server.post(
    "fs/createFilesystem",
    { body: formData },
  );
  return await response.json() as FilesystemFolderInfo;
}

interface WorkspaceRenameModel {
  FilesystemId: string;
  Name: string;
}
export async function renameFilesystem(options: { id: string; name: string }) {
  const model: WorkspaceRenameModel = {
    FilesystemId: options.id,
    Name: options.name,
  };
  const formData = objectToFormData(model);
  const response = await server.post("fs/renameFilesystem", { body: formData });
  return await response.json() as ResponseObject;
}

export async function favoriteFilesystem(options: { id: string; isFavorite: boolean }) {
  const formData = objectToFormData({ filesystemId: options.id, favorite: options.isFavorite });
  const response = await server.post("fs/favoriteWorkspace", { body: formData });
  return await response.json() as ResponseObject;
}

export async function archiveFilesystem(options: { id: string; archive: boolean }) {
  const formData = objectToFormData({ filesystemId: options.id, archive: options.archive });
  const response = await server.post("fs/archiveWorkspace", { body: formData });
  return await response.json() as ResponseObject;
}

export async function changeFilesystemOwner(options: { id: string; newOwnerId: string }) {
  const formData = objectToFormData({ filesystemId: options.id, newOwnerId: options.newOwnerId });
  const response = await server.post("fs/updateFilesystemOwner", { body: formData });
  return await response.json() as FilesystemJs;
}

export interface RenameFolderModel {
  Name: string;
  FolderId: string;
}
export async function renameFolder(options: { id: string; name: string }) {
  const model: RenameFolderModel = {
    FolderId: options.id,
    Name: options.name,
  };
  const formData = objectToFormData(model);
  const response = await server.post("fs/renameFolder", { body: formData });
  return await response.json() as ResponseObject;
}

export type CreateFolderModel = IdOrFilesystemId & { Name?: string; insertLocation?: InsertLocation; insertAfter?: string };

export async function createFolder(model: CreateFolderModel) {
  const formData = objectToFormData(model);
  const response = await server.post("fs/createFolder", { body: formData });
  return await response.json() as FolderRowJs;
}

export async function deleteFilesystem(filesystemId: string) {
  const formData = objectToFormData({
    filesystemId,
  });

  const response = await server.post("fs/deleteFilesystem", { body: formData });
  return await response.json() as ResponseObject;
}

export async function deleteFilesystemItems(deleteItems: DeleteFilesystemItemsRequest) {
  const formData = objectToFormData(deleteItems);
  const response = await server.post("fs/deleteFilesystemItems", { body: formData });
  return await response.json() as ResponseObject;
}

export async function moveFilesystemItems(moveItems: MoveFilesystemItemsRequest) {
  const formData = objectToFormData(moveItems);
  const response = await server.post("fs/moveFilesystemItems", { body: formData });
  return await response.json() as MoveFilesystemItemsResponse;
}

export interface ShareFilesystemParams {
  filesystemId: string;
  userEmails: string[];
  viewerOnly: boolean;
  ignoreAccepted: boolean;
  ignorePending: boolean;
  assignSeats: boolean;
  inviteMessage?: string;
  recaptchaToken?: string;
}
export interface ShareFilesystemModelBase {
  filesystemId: string;
  userEmails: string;
  viewerOnly: boolean;
  ignoreAccepted: boolean;
  ignorePending: boolean;
  assignSeats: boolean;
}

export type ShareFilesystemModelProtected = ShareFilesystemModelBase & {
  inviteMessage: string;
  recaptchaToken: string;
};

export type ShareFilesystemModel = ShareFilesystemModelBase | ShareFilesystemModelProtected;

export async function shareWorkspace(params: ShareFilesystemParams) {
  const model = {
    filesystemId: params.filesystemId,
    userEmails: params.userEmails.join(","),
    inviteMessage: params.inviteMessage,
    assignSeats: params.assignSeats,
    ignoreAccepted: params.ignoreAccepted,
    ignorePending: params.ignorePending,
    viewerOnly: params.viewerOnly,
    recaptchaToken: params.recaptchaToken,
  } satisfies ShareFilesystemModel;

  const formData = objectToFormData(model);
  // when passing invite message recaptcha-protected endpoint should be used instead
  const url = model.inviteMessage ? "fs/inviteUsersToFilesystem" : "fs/shareFilesystem";
  const response = await server.post(url, { body: formData });

  return await response.json() as ShareFilesystemResultResponse;
}

export interface UnshareFilesystemModel {
  filesystemId: string;
  usersToRemove: string[];
  removeAllUsers: boolean;
}

export async function unshareWorkspace(params: { filesystemId: string; userIds: string[] }) {
  const model: UnshareFilesystemModel = {
    filesystemId: params.filesystemId,
    usersToRemove: params.userIds,
    removeAllUsers: false,
  };

  const formData = objectToFormData(model);
  const response = await server.post("fs/unshareFilesystem", { body: formData });
  return await response.json() as ResponseObject;
}

export async function joinWorkspace(filesystemId: string) {
  const model = {
    filesystemId,
  };

  const formData = objectToFormData(model);
  const response = await server.post("fs/joinToFilesystem", { body: formData });
  return await response.json() as ResponseObject;
}

export async function leaveWorkspace(filesystemId: string) {
  const model = {
    filesystemId,
  };

  const formData = objectToFormData(model);
  const response = await server.post("fs/unjoinShare", { body: formData });
  return await response.json() as ResponseObject;
}

export interface SetFilesystemReviewerModel {
  filesystemId: string;
  userToChange: string;
  isReviewOnly: boolean;
}

export async function workspaceMemberSetPermissions(params: {
  filesystemId: string;
  userId: string;
  permissionType: WorkspacePermissionType;
}) {
  const model: SetFilesystemReviewerModel = {
    userToChange: params.userId,
    filesystemId: params.filesystemId,
    isReviewOnly: params.permissionType === "view-only",
  };

  const formData = objectToFormData(model);
  const response = await server.post("fs/setReviewerOnly", { body: formData });

  return await response.json() as ResponseObject;
}

export interface ChangeFilesystemDefaultMappingTypeModel {
  filesystemId: string;
  newMappingType: FilesystemDefaultMappingType;
}

export async function workspaceOrganizationSetPermissions(params: {
  filesystemId: string;
  permissionType: SubInstanceOrganizationPermissionType;
}) {
  const model: ChangeFilesystemDefaultMappingTypeModel = {
    filesystemId: params.filesystemId,
    newMappingType: toCloudDefaultMappingType(params.permissionType),
  };

  const formData = objectToFormData(model);

  const response = await server.post("fs/changeFilesystemDefaultMappingType", { body: formData });
  return await response.json() as ResponseObject;
}

interface FilesystemSetUserOnlyModel {
  filesystemId: string;
  userOnly: boolean;
}
export async function workspaceSetUserOnly(params: {
  filesystemId: string;
  shareLinkAccessType: ShareLinkAccessType;
}) {
  const model: FilesystemSetUserOnlyModel = {
    filesystemId: params.filesystemId,
    userOnly: toCloudUserViewOnly(params.shareLinkAccessType),
  };

  const formData = objectToFormData(model);

  const response = await server.post("fs/setUserOnly", { body: formData });
  return await response.json() as ResponseObject;
}

export async function getUserSearchData({ signal }: RequestOptions = {}) {
  const response = await server.get("fs/getUserSearchData2", { signal });
  return await response.json() as FilesystemSearchDataJs;
}

export async function getUserInvitations({ signal }: RequestOptions = {}) {
  const response = await server.get("fs/getUserInvitations", { signal });
  return await response.json() as InvitationJs[];
}

interface FilesystemInvitationRespond {
  filesystemId: string;
  accept: boolean;
}

export async function inviteRespond(params: { filesystemId: string; accept: boolean }) {
  const model: FilesystemInvitationRespond = {
    filesystemId: params.filesystemId,
    accept: params.accept,
  };

  const formData = objectToFormData(model);

  const response = await server.post("fs/inviteRespond", { body: formData });
  return await response.json() as ResponseObject;
}

export async function requestInvite(params: {
  shortcut: string;
}) {
  const formData = objectToFormData({ shortcut: params.shortcut });

  const response = await server.post("fs/requestInvite", { body: formData });
  return await response.json() as RequestInviteResponse;
}

export async function getSuggestedWorkspaces(options: { organizationId: string }, { signal }: RequestOptions = {}) {
  const searchParams = {
    organizationId: options.organizationId,
  };
  const response = await server.get("fs/getSuggestedWorkspaces", { searchParams, signal });
  return await response.json() as FilesystemJs[];
}
